import { ICoreContext } from '@msdyn365-commerce/core';
import { IGetAllProductsResult } from '../actions/create-order/get-all-products.action';
import { IPostCartItems } from '../actions/create-order/post-cart.action';
import { Separator } from '../actions/user-preferences/get-localization.action';
import { CREATE_ORDER_ALLOW_LEADING_ZEROS, CREATE_ORDER_DEFAULT_QUANTITY_DECIMAL_SCALE } from '../configuration';
import { LOCALIZATION_DEFAULT_NUMBER } from '../configuration/localization';
import { ILxsOrderData } from '../modules/lxs-create-order/lxs-create-order.data';
import { ILocalizationState } from '../states/localization-state';

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Quantity inputs can override the historical 3-decimal default with a unit-specific scale.
const normalizeDecimalScale = (decimalScale?: number): number => {
    if (!Number.isInteger(decimalScale) || decimalScale === undefined || decimalScale < 0) {
        return CREATE_ORDER_DEFAULT_QUANTITY_DECIMAL_SCALE;
    }

    return decimalScale;
};

/**
 * Formats a number.
 * @param context - Core context.
 * @param date - Number to display.
 * @returns String value.
 */
// Example usage: formatNumber(this.props.context, new Date(CreatedDate)

export const formatNumber = (context: ICoreContext, number: number | undefined) => {
    if (number === undefined) {
        return '';
    }

    /**
     * don't do this, since it adds separators in unwanted places
    const numberOptions: INumberFormatOptions = { style: 'decimal' };
    const formattedNumber = context.cultureFormatter.formatNumber(number, numberOptions);
    //const formattedNumber = number.toString().replace(/\.\d+$/, '');
    */
    // Simply drop decimal points and convert to string
    // return Math.trunc(number).toString();
    return number.toString();
};

export const formatSliceNumber = (input: string) => {
    const dot = input.indexOf('.');
    if (dot === -1) {
        return input;
    }
    return input.slice(0, dot + 1 + CREATE_ORDER_DEFAULT_QUANTITY_DECIMAL_SCALE);
};

export const formatNumberLocaleStatic = (input: string | number, localizationState: ILocalizationState, decimalScale?: number): string => {
    const maxDecimalScale = normalizeDecimalScale(decimalScale);
    const decimalSeparator = localizationState.numberLocale
        ? localizationState.numberLocale.decimalSeparator
        : LOCALIZATION_DEFAULT_NUMBER.decimalSeparator;
    const thousandSeparator = localizationState.numberLocale
        ? localizationState.numberLocale.thousandSeparator
        : LOCALIZATION_DEFAULT_NUMBER.thousandSeparator;

    if (input === '0' || input === 0) {
        return '';
    }

    if (typeof input === 'number') {
        input = input.toString();
    }

    // Check if the decimal separator in the input matches the defined decimalSeparator
    if (decimalSeparator === Separator.Comma && input.includes(Separator.Dot)) {
        input = input.replace(Separator.Dot, Separator.Comma);
    } else if (decimalSeparator === Separator.Dot && input.includes(Separator.Comma)) {
        input = input.replace(Separator.Comma, Separator.Dot);
    }

    // Extract integer and decimal parts
    let [integerPart, decimalPart] = input.split(decimalSeparator);

    // Remove leading zeros unless it's a single zero
    if (!CREATE_ORDER_ALLOW_LEADING_ZEROS) {
        integerPart = integerPart.replace(/^0+(?=\d)/, '');
    }

    // Apply thousand separator
    if (thousandSeparator && integerPart) {
        // eslint-disable-next-line security/detect-unsafe-regex
        integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
    }

    // Limit decimal places
    if (decimalPart) {
        decimalPart = decimalPart.slice(0, maxDecimalScale).replace(/0+$/, '');
    }

    // Combine integer and decimal parts
    return decimalPart ? `${integerPart}${decimalSeparator}${decimalPart}` : integerPart;
};

export const formatNumberLocaleDynamic = (input: string | Number, localizationState: ILocalizationState, decimalScale?: number): string => {
    const maxDecimalScale = normalizeDecimalScale(decimalScale);
    const decimalSeparator = localizationState.numberLocale
        ? localizationState.numberLocale.decimalSeparator
        : LOCALIZATION_DEFAULT_NUMBER.decimalSeparator;
    const thousandSeparator = localizationState.numberLocale
        ? localizationState.numberLocale.thousandSeparator
        : LOCALIZATION_DEFAULT_NUMBER.thousandSeparator;

    if (typeof input === 'number') {
        input = input.toString();
    }

    // When a unit allows no decimals, the input filter must reject the locale decimal separator entirely.
    const sanitizedDecimalSeparator = maxDecimalScale > 0 ? escapeRegExp(decimalSeparator) : '';

    // Allow only numbers and the configured decimal separator.
    // eslint-disable-next-line security/detect-non-literal-regexp
    let formattedValue = (input as string).replace(new RegExp(`[^0-9${sanitizedDecimalSeparator}]`, 'g'), '');

    // Prevent multiple decimal separators
    const parts = formattedValue.split(decimalSeparator);
    if (parts.length > 2) {
        formattedValue = parts[0] + decimalSeparator + parts.slice(1).join('');
    }

    // Extract integer and decimal parts
    let [integerPart, decimalPart] = formattedValue.split(decimalSeparator);

    // Remove leading zeros unless it's a single zero
    if (!CREATE_ORDER_ALLOW_LEADING_ZEROS) {
        integerPart = integerPart.replace(/^0+(?=\d)/, '');
    }

    // Apply thousand separator (but not when user is typing decimal part)
    if (thousandSeparator && integerPart) {
        // eslint-disable-next-line security/detect-unsafe-regex
        integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
    }

    // Limit decimal places
    if (decimalPart) {
        decimalPart = decimalPart.slice(0, maxDecimalScale);
    }

    // Combine integer and decimal parts
    return decimalPart !== undefined ? `${integerPart}${decimalSeparator}${decimalPart}` : integerPart;
};

export const removeTrailingZeros = (number: string) => {
    return parseFloat(number.replace(/,/g, '.'));
};

export const checkIfValidNumber = (number: string | number | undefined): boolean => {
    if (number && Number(number) > 0) {
        return true;
    } else {
        return false;
    }
};

export const parseLocalizedQuantity = (rawValue: string | number | undefined, localizationState: ILocalizationState): number => {
    const { numberLocale } = localizationState;

    if (typeof rawValue === 'number') {
        return Number.isFinite(rawValue) ? rawValue : 0;
    }

    if (!rawValue) {
        return 0;
    }

    const removeAll = (source: string, token: string) => (token ? source.split(token).join('') : source);
    const sanitized = rawValue.trim().replace(/\s/g, '');

    const lastComma = sanitized.lastIndexOf(',');
    const lastDot = sanitized.lastIndexOf('.');

    // Use the right-most separator as decimal separator when both are present.
    // This also supports users typing either 5,55 or 5.55 independent of locale.
    const detectedDecimalSeparator =
        lastComma >= 0 && lastDot >= 0 ? (lastComma > lastDot ? ',' : '.') : lastComma >= 0 ? ',' : lastDot >= 0 ? '.' : '';

    let normalized = sanitized;
    if (detectedDecimalSeparator) {
        const detectedThousandsSeparator = detectedDecimalSeparator === ',' ? '.' : ',';
        normalized = removeAll(normalized, detectedThousandsSeparator);

        if (numberLocale.thousandSeparator && numberLocale.thousandSeparator !== detectedDecimalSeparator) {
            normalized = removeAll(normalized, numberLocale.thousandSeparator);
        }

        normalized = normalized.replace(detectedDecimalSeparator, '.');
    } else if (numberLocale.thousandSeparator) {
        normalized = removeAll(normalized, numberLocale.thousandSeparator);
    }

    const parsed = Number(normalized);

    return Number.isFinite(parsed) ? parsed : 0;
};

// Preserve user input as much as possible while still enforcing locale-specific formatting and unit-specific decimal limits.
export const toQuantityNumber = (input: string | number | undefined, localizationState: ILocalizationState) =>
    parseLocalizedQuantity(input, localizationState);

export const getAllowedDecimalsForUnit = (unit: string | undefined, relevantProduct: IGetAllProductsResult | undefined): number => {
    const selectedUnit = relevantProduct?.AlternativeUnits?.find(alternativeUnit => alternativeUnit.Unit === unit);
    const decimalsAllowed = Number(selectedUnit?.DecimalsAllowed);

    return Number.isInteger(decimalsAllowed) && decimalsAllowed >= 0 ? decimalsAllowed : CREATE_ORDER_DEFAULT_QUANTITY_DECIMAL_SCALE;
};

// Switching into a coarse unit like BX, PAL etc. should keep a positive quantity visible instead of collapsing to an empty zero.
export const getMinimumRepresentableQuantity = (unit: string | undefined, relevantProduct: IGetAllProductsResult | undefined): string => {
    const allowedDecimals = getAllowedDecimalsForUnit(unit, relevantProduct);

    if (allowedDecimals === 0) {
        return '1';
    }

    return (1 / Math.pow(10, allowedDecimals)).toFixed(allowedDecimals);
};

// Clamps a quantity value to the allowed decimal places for the unit, and returns an empty string if the result is zero or invalid.
export const clampQuantityValue = (
    quantity: string | number | undefined,
    unit: string | undefined,
    relevantProduct: IGetAllProductsResult | undefined
): string => {
    if (quantity === undefined || quantity === '') {
        return '';
    }

    const normalizedQuantity = `${quantity}`.trim().replace(',', '.');
    if (!normalizedQuantity) {
        return '';
    }

    const parsedQuantity = Number(normalizedQuantity);

    if (!Number.isFinite(parsedQuantity)) {
        return '0';
    }

    const allowedDecimals = getAllowedDecimalsForUnit(unit, relevantProduct);
    if (allowedDecimals === 0) {
        return Math.round(parsedQuantity).toString();
    }

    const roundedQuantity = Number(parsedQuantity.toFixed(allowedDecimals));

    return Number.isFinite(roundedQuantity) ? roundedQuantity.toString() : '0';
};

export const formatQuantityDisplay = (
    quantity: string | number | undefined,
    unit: string | undefined,
    relevantProduct: IGetAllProductsResult | undefined,
    localizationState: ILocalizationState
): string => {
    if (quantity === undefined || quantity === '') {
        return '';
    }

    const normalizedQuantity = clampQuantityValue(quantity, unit, relevantProduct);

    return normalizedQuantity === ''
        ? ''
        : formatNumberLocaleStatic(normalizedQuantity, localizationState, getAllowedDecimalsForUnit(unit, relevantProduct));
};

export const getQuantityContext = (
    product: IPostCartItems,
    productsData: IGetAllProductsResult[] | undefined,
    orderData: ILxsOrderData
) => {
    const relevantProduct = productsData?.find(
        candidate => candidate.ProductNumber === product.ProductNumber && candidate.Incoterm1 === orderData.orderHeader?.Incoterm1
    );

    return {
        relevantProduct,
        unitOfMeasure: product.UnitOfMeasure,
        quantityDecimalScale: getAllowedDecimalsForUnit(product.UnitOfMeasure, relevantProduct)
    };
};
